/**
 * @module placeholderService
 * @description Shared placeholder resolution for project metadata.
 *
 * Parses YAML metadata and resolves {{PLACEHOLDER}} tokens in markdown text.
 * Used by:
 * - placeholderPlugin.ts (WYSIWYG badge decorations)
 * - Export functions (EPUB, ZIP, individual doc exports)
 *
 * Also supports optional blocks [[ ... ]] that are removed when all
 * their placeholders resolve to empty values.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface ParsedMetadata {
  title: string;
  subtitle: string;
  originalTitle: string;
  lang: string;
  publisher: string;
  rights: string;
  date: string;
  publishedPrint: string;
  place: string;
  serie: string;
  price: string;
  source: string;
  dedication: string;
  description: string;
  funding: string;
  editions: string;
  coverNote: string;
  translatorDisplay: string;
  criticalDisplay: string;
  isbnPrint: string;
  accessibilitySummary: string;
  author: string;
  isbnEbook: string;
  issn: string;
  /** Custom fields from YAML: key (YAML key) → value */
  customFields: Record<string, string>;
}

type PlaceholderResolver = (meta: ParsedMetadata) => string;

// ============================================================================
// PLACEHOLDER MAP
// ============================================================================

const PLACEHOLDER_MAP: Record<string, PlaceholderResolver> = {
  TITLE: (m) => m.title,
  SUBTITLE: (m) => m.subtitle,
  ORIGINAL_TITLE: (m) => m.originalTitle,
  LANG: (m) => m.lang,
  AUTHOR: (m) => m.author,
  PUBLISHER: (m) => m.publisher,
  RIGHTS: (m) => m.rights,
  DATE: (m) => m.date,
  PUBLISHED_PRINT: (m) => m.publishedPrint,
  PLACE: (m) => m.place,
  SERIE: (m) => m.serie,
  PRICE: (m) => m.price,
  SOURCE: (m) => m.source,
  DEDICATION: (m) => m.dedication,
  DESCRIPTION: (m) => m.description,
  FUNDING: (m) => m.funding,
  EDITIONS: (m) => m.editions,
  COVER_NOTE: (m) => m.coverNote,
  TRANSLATOR: (m) => m.translatorDisplay,
  TRANSLATOR_DISPLAY: (m) => m.translatorDisplay,
  CRITICAL: (m) => m.criticalDisplay,
  CRITICAL_DISPLAY: (m) => m.criticalDisplay,
  ISBN_PRINT: (m) => m.isbnPrint,
  ISBN_EBOOK: (m) => m.isbnEbook,
  ISSN: (m) => m.issn,
  ACCESSIBILITY_SUMMARY: (m) => m.accessibilitySummary,
};

// ============================================================================
// YAML PARSING
// ============================================================================

/**
 * Parse project YAML metadata into a flat structure for placeholder resolution.
 */
export function parseProjectMetadata(yaml: string | undefined): ParsedMetadata {
  const meta: ParsedMetadata = {
    title: '', subtitle: '', originalTitle: '', lang: '', publisher: '',
    rights: '', date: '', publishedPrint: '', place: '', serie: '',
    price: '', source: '', dedication: '', description: '', funding: '',
    editions: '', coverNote: '', translatorDisplay: '', criticalDisplay: '',
    isbnPrint: '', accessibilitySummary: '', author: '', isbnEbook: '', issn: '',
    customFields: {},
  };

  if (!yaml) return meta;

  const lines = yaml.split('\n');
  let i = 0;

  const parseMultiline = (startIdx: number): { value: string; endIdx: number } => {
    let value = '';
    let idx = startIdx;
    while (idx < lines.length) {
      const line = lines[idx];
      if (line.match(/^\s{2,}/) || line.trim() === '') {
        value += (value ? '\n' : '') + line.replace(/^\s{2}/, '');
        idx++;
      } else if (!line.startsWith(' ') && line.includes(':')) {
        break;
      } else {
        idx++;
      }
    }
    return { value: value.trim(), endIdx: idx - 1 };
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed || trimmed.startsWith('#') || trimmed === '---') {
      i++;
      continue;
    }

    const colonIdx = trimmed.indexOf(':');
    if (colonIdx > 0) {
      const key = trimmed.slice(0, colonIdx).trim();
      const value = trimmed.slice(colonIdx + 1).trim();

      switch (key) {
        case 'lang': meta.lang = value.replace(/"/g, ''); break;
        case 'publisher': meta.publisher = value.replace(/"/g, ''); break;
        case 'date': meta.date = value.replace(/"/g, ''); break;
        case 'source': meta.source = value.replace(/"/g, ''); break;
        case 'original-title': meta.originalTitle = value.replace(/"/g, ''); break;
        case 'published-print': meta.publishedPrint = value.replace(/"/g, ''); break;
        case 'isbn-print': meta.isbnPrint = value.replace(/"/g, ''); break;
        case 'price': meta.price = value.replace(/"/g, ''); break;
        case 'place': meta.place = value.replace(/"/g, ''); break;
        case 'serie': meta.serie = value.replace(/"/g, ''); break;
        case 'rights':
        case 'dedication':
        case 'description':
        case 'funding':
        case 'editions':
        case 'cover-note':
        case 'translator-display':
        case 'critical-display':
        case 'accessibilitySummary': {
          const fieldMap: Record<string, keyof ParsedMetadata> = {
            'rights': 'rights', 'dedication': 'dedication', 'description': 'description',
            'funding': 'funding', 'editions': 'editions', 'cover-note': 'coverNote',
            'translator-display': 'translatorDisplay', 'critical-display': 'criticalDisplay',
            'accessibilitySummary': 'accessibilitySummary',
          };
          const field = fieldMap[key];
          if (field) {
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              (meta as any)[field] = multiVal;
              i = endIdx;
            } else {
              (meta as any)[field] = value.replace(/"/g, '');
            }
          }
          break;
        }
        case 'title': {
          if (!value) {
            i++;
            while (i < lines.length && lines[i].startsWith('  ')) {
              const tline = lines[i].trim();
              if (tline.startsWith('- type: main')) {
                i++;
                if (i < lines.length && lines[i].includes('text:')) {
                  meta.title = lines[i].split('text:')[1].trim().replace(/"/g, '');
                }
              } else if (tline.startsWith('- type: subtitle')) {
                i++;
                if (i < lines.length && lines[i].includes('text:')) {
                  meta.subtitle = lines[i].split('text:')[1].trim().replace(/"/g, '');
                }
              }
              i++;
            }
            i--;
          } else {
            meta.title = value.replace(/"/g, '');
          }
          break;
        }
        case 'creator': {
          i++;
          let foundAuthor = false;
          let currentRole = '';
          while (i < lines.length && lines[i].startsWith('  ')) {
            const cline = lines[i].trim();
            if (cline.startsWith('- role:')) {
              currentRole = cline.split(':')[1].trim();
            } else if (cline.startsWith('text:')) {
              const name = cline.split(':').slice(1).join(':').trim().replace(/"/g, '');
              if (!foundAuthor && currentRole === 'author') {
                meta.author = name;
                foundAuthor = true;
              }
            }
            i++;
          }
          i--;
          break;
        }
        case 'identifier': {
          i++;
          let currentScheme = '';
          while (i < lines.length && lines[i].startsWith('  ')) {
            const idline = lines[i].trim();
            if (idline.startsWith('- scheme:')) {
              currentScheme = idline.split(':')[1].trim().replace(/"/g, '');
            } else if (idline.startsWith('text:')) {
              const text = idline.split(':').slice(1).join(':').trim().replace(/"/g, '');
              if (currentScheme === 'ISBN' && !meta.isbnEbook) {
                meta.isbnEbook = text;
              } else if (currentScheme === 'ISSN' && !meta.issn) {
                meta.issn = text;
              }
            }
            i++;
          }
          i--;
          break;
        }
        default: {
          // Collect unknown keys as custom fields
          // Skip known structural keys that aren't handled as simple fields
          const knownStructural = new Set([
            'subject', 'accessModes', 'accessModeSufficient',
            'accessibilityHazards', 'accessibilityFeatures', 'cover-image'
          ]);
          if (key && key !== '---' && !knownStructural.has(key)) {
            let fieldValue = '';
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              fieldValue = multiVal;
              i = endIdx;
            } else {
              fieldValue = value.replace(/"/g, '');
            }
            if (fieldValue) {
              // Store with original YAML key
              meta.customFields[key] = fieldValue;
            }
          }
          break;
        }
      }
    }
    i++;
  }

  return meta;
}

// ============================================================================
// PLACEHOLDER RESOLUTION
// ============================================================================

/**
 * Resolve a single placeholder name to its value.
 * Falls back to custom fields if no built-in resolver matches.
 * Custom field lookup: {{MY_FIELD}} → YAML key "my-field" or "my_field"
 */
export function resolvePlaceholder(name: string, metadata: ParsedMetadata): string {
  const resolver = PLACEHOLDER_MAP[name];
  if (resolver) return resolver(metadata);

  // Try custom fields: convert PLACEHOLDER_NAME → yaml-key
  const yamlKey = name.toLowerCase().replace(/_/g, '-');
  if (metadata.customFields[yamlKey]) return metadata.customFields[yamlKey];

  // Also try with underscores
  const yamlKeyUnderscore = name.toLowerCase().replace(/_/g, '_');
  if (metadata.customFields[yamlKeyUnderscore]) return metadata.customFields[yamlKeyUnderscore];

  // Also try exact original key (case-insensitive search)
  for (const [k, v] of Object.entries(metadata.customFields)) {
    if (k.toUpperCase().replace(/-/g, '_') === name) return v;
  }

  return '';
}

/**
 * Get the placeholder map (for use in the decoration plugin).
 */
export function getPlaceholderMap(): Record<string, PlaceholderResolver> {
  return PLACEHOLDER_MAP;
}

/**
 * Resolve optional blocks [[ ... ]].
 * A block is removed if ALL its placeholders resolve to empty values.
 * If at least one placeholder has content, the brackets are removed
 * but the content is kept.
 */
function resolveOptionalBlocks(text: string, metadata: ParsedMetadata): string {
  const placeholderPattern = /\{\{([A-Z_]+)\}\}/g;

  return text.replace(/\[\[\s*([\s\S]*?)\s*\]\]/g, (_match, inner: string) => {
    const placeholders = [...inner.matchAll(placeholderPattern)];

    if (placeholders.length === 0) {
      // No placeholders inside — keep the content
      return inner;
    }

    // Check if ALL placeholders in this block are empty
    const allEmpty = placeholders.every((m) => {
      return !resolvePlaceholder(m[1], metadata).trim();
    });

    return allEmpty ? '' : inner;
  });
}

/**
 * Check whether a placeholder name is recognized (built-in or custom field).
 */
export function isKnownPlaceholder(name: string, metadata: ParsedMetadata): boolean {
  if (PLACEHOLDER_MAP[name]) return true;

  const yamlKey = name.toLowerCase().replace(/_/g, '-');
  if (metadata.customFields[yamlKey]) return true;

  const yamlKeyUnderscore = name.toLowerCase();
  if (metadata.customFields[yamlKeyUnderscore]) return true;

  for (const k of Object.keys(metadata.customFields)) {
    if (k.toUpperCase().replace(/-/g, '_') === name) return true;
  }

  return false;
}

/**
 * Replace all {{PLACEHOLDER}} tokens in text with their resolved values.
 * Also handles optional blocks [[ ... ]].
 */
export function resolveAllPlaceholders(text: string, metadata: ParsedMetadata): string {
  // 1. Resolve optional blocks first
  text = resolveOptionalBlocks(text, metadata);

  // 2. Replace all {{PLACEHOLDER}} tokens (built-in + custom fields)
  // Known placeholders with empty values → empty string
  // Unknown placeholders → kept as-is
  text = text.replace(/\{\{([A-Z_]+)\}\}/g, (_match, name: string) => {
    if (isKnownPlaceholder(name, metadata)) {
      return resolvePlaceholder(name, metadata);
    }
    return _match;
  });

  // 3. Remove empty fenced divs (e.g. ::: {.class}\n\n::: after placeholder removal)
  text = text.replace(/^:::\s*\{[^}]*\}\s*\n(?:\s*\n)*:::\s*$/gm, '');

  // 4. Clean up excessive blank lines (3+ → 2)
  text = text.replace(/\n{3,}/g, '\n\n');

  return text;
}

/**
 * Convenience: parse YAML metadata and resolve all placeholders in one call.
 */
export function patchPlaceholders(markdown: string, yamlMetadata: string | undefined): string {
  const metadata = parseProjectMetadata(yamlMetadata);
  return resolveAllPlaceholders(markdown, metadata);
}

// ============================================================================
// YAML FIELD UPDATE (for inline editing)
// ============================================================================

/**
 * Mapping from placeholder name to YAML key(s).
 * Simple fields map to a single YAML key.
 * Complex fields (AUTHOR, ISBN_EBOOK, ISSN) are structured and handled specially.
 */
const PLACEHOLDER_TO_YAML_KEY: Record<string, string | null> = {
  TITLE: null,           // structured (title block)
  SUBTITLE: null,        // structured (title block)
  AUTHOR: null,          // structured (creator block)
  ISBN_EBOOK: null,      // structured (identifier block)
  ISSN: null,            // structured (identifier block)
  ORIGINAL_TITLE: 'original-title',
  LANG: 'lang',
  PUBLISHER: 'publisher',
  RIGHTS: 'rights',
  DATE: 'date',
  PUBLISHED_PRINT: 'published-print',
  PLACE: 'place',
  SERIE: 'serie',
  PRICE: 'price',
  SOURCE: 'source',
  DEDICATION: 'dedication',
  DESCRIPTION: 'description',
  FUNDING: 'funding',
  EDITIONS: 'editions',
  COVER_NOTE: 'cover-note',
  TRANSLATOR: 'translator-display',
  TRANSLATOR_DISPLAY: 'translator-display',
  CRITICAL: 'critical-display',
  CRITICAL_DISPLAY: 'critical-display',
  ISBN_PRINT: 'isbn-print',
  ACCESSIBILITY_SUMMARY: 'accessibilitySummary',
};

/**
 * Update a simple (single-line) YAML field in the metadata string.
 * If the field exists, replace its value. If not, append it.
 * Returns the updated YAML string, or null if the field is structured
 * and cannot be updated with this simple approach.
 */
export function updateYamlField(
  yaml: string | undefined,
  placeholderName: string,
  newValue: string
): string | null {
  const yamlKey = PLACEHOLDER_TO_YAML_KEY[placeholderName];

  // Structured fields require special handling
  if (yamlKey === null) {
    return updateStructuredYamlField(yaml || '', placeholderName, newValue);
  }

  // Custom field (not in the built-in map): derive YAML key from name
  if (yamlKey === undefined) {
    const customYamlKey = placeholderName.toLowerCase().replace(/_/g, '-');
    return updateSimpleYamlField(yaml || '', customYamlKey, newValue);
  }

  return updateSimpleYamlField(yaml || '', yamlKey, newValue);
}

/**
 * Update a simple (single-line) YAML field by key.
 * If the key exists, replace its value. If not, append it.
 */
function updateSimpleYamlField(yaml: string, yamlKey: string, newValue: string): string {
  const lines = yaml.split('\n');
  let found = false;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx > 0) {
      const key = trimmed.slice(0, colonIdx).trim();
      if (key === yamlKey) {
        const oldValue = trimmed.slice(colonIdx + 1).trim();
        if (oldValue === '|' || oldValue === '>') {
          lines[i] = `${yamlKey}: "${newValue}"`;
          while (i + 1 < lines.length && (lines[i + 1].match(/^\s{2,}/) || lines[i + 1].trim() === '')) {
            if (lines[i + 1].trim() !== '' && !lines[i + 1].startsWith(' ') && lines[i + 1].includes(':')) break;
            lines.splice(i + 1, 1);
          }
        } else {
          lines[i] = `${yamlKey}: "${newValue}"`;
        }
        found = true;
        break;
      }
    }
  }

  if (!found) {
    lines.push(`${yamlKey}: "${newValue}"`);
  }

  return lines.join('\n');
}

/**
 * Handle structured fields: title, subtitle, author, isbn_ebook, issn.
 */
function updateStructuredYamlField(yaml: string, placeholderName: string, newValue: string): string | null {
  const lines = yaml.split('\n');

  switch (placeholderName) {
    case 'TITLE':
    case 'SUBTITLE': {
      const targetType = placeholderName === 'TITLE' ? 'main' : 'subtitle';
      let inTitle = false;
      let inTargetEntry = false;

      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (trimmed.startsWith('title:')) {
          const val = trimmed.slice(6).trim();
          if (val && placeholderName === 'TITLE') {
            // Simple title: format — just replace
            lines[i] = `title: "${newValue}"`;
            return lines.join('\n');
          }
          inTitle = true;
          continue;
        }
        if (inTitle) {
          if (!lines[i].startsWith('  ') && lines[i].trim() && lines[i].includes(':')) {
            inTitle = false;
            break;
          }
          if (trimmed === `- type: ${targetType}`) {
            inTargetEntry = true;
          } else if (inTargetEntry && trimmed.startsWith('text:')) {
            lines[i] = `    text: "${newValue}"`;
            return lines.join('\n');
          } else if (trimmed.startsWith('- type:')) {
            inTargetEntry = false;
          }
        }
      }
      break;
    }
    case 'AUTHOR': {
      let inCreator = false;
      let currentRole = '';

      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (trimmed.startsWith('creator:')) {
          inCreator = true;
          continue;
        }
        if (inCreator) {
          if (!lines[i].startsWith('  ') && lines[i].trim() && lines[i].includes(':')) {
            inCreator = false;
            break;
          }
          if (trimmed.startsWith('- role:')) {
            currentRole = trimmed.split(':')[1].trim();
          } else if (trimmed.startsWith('text:') && currentRole === 'author') {
            lines[i] = `    text: "${newValue}"`;
            return lines.join('\n');
          }
        }
      }
      break;
    }
    case 'ISBN_EBOOK': {
      let inIdentifier = false;
      let currentScheme = '';

      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (trimmed.startsWith('identifier:')) {
          inIdentifier = true;
          continue;
        }
        if (inIdentifier) {
          if (!lines[i].startsWith('  ') && lines[i].trim() && lines[i].includes(':')) {
            inIdentifier = false;
            break;
          }
          if (trimmed.startsWith('- scheme:')) {
            currentScheme = trimmed.split(':')[1].trim().replace(/"/g, '').toUpperCase();
          } else if (trimmed.startsWith('text:') && currentScheme === 'ISBN') {
            lines[i] = `    text: "${newValue}"`;
            return lines.join('\n');
          }
        }
      }
      break;
    }
    case 'ISSN': {
      let inIdentifier = false;
      let currentScheme = '';

      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (trimmed.startsWith('identifier:')) {
          inIdentifier = true;
          continue;
        }
        if (inIdentifier) {
          if (!lines[i].startsWith('  ') && lines[i].trim() && lines[i].includes(':')) {
            inIdentifier = false;
            break;
          }
          if (trimmed.startsWith('- scheme:')) {
            currentScheme = trimmed.split(':')[1].trim().replace(/"/g, '').toUpperCase();
          } else if (trimmed.startsWith('text:') && currentScheme === 'ISSN') {
            lines[i] = `    text: "${newValue}"`;
            return lines.join('\n');
          }
        }
      }
      break;
    }
  }

  // Could not find field to update
  return null;
}

// ============================================================================
// BIBLIOGRAPHY PLACEHOLDER (for export)
// ============================================================================

/**
 * Regex to match Pandoc citation patterns (same as citationPlugin)
 */
const CITATION_REGEX = /\[@([a-zA-Z0-9_:-]+)(?:,\s*([^\]]+))?\]/g;

/**
 * Regex to match bibliography placeholder
 */
const BIBLIOGRAPHY_REGEX = /\{\{bibliography\}\}/gi;

/**
 * Extract all unique citation keys from markdown text
 */
export function extractCitationKeysFromMarkdown(markdown: string): string[] {
  const keys = new Set<string>();
  let match;

  // Reset regex state
  const regex = new RegExp(CITATION_REGEX.source, 'g');

  while ((match = regex.exec(markdown)) !== null) {
    keys.add(match[1]);
  }

  return Array.from(keys);
}

/**
 * Strip HTML tags from text, preserving content
 */
function stripHtmlTags(html: string): string {
  return html
    .replace(/<[^>]+>/g, '')  // Remove HTML tags
    .replace(/&amp;/g, '&')   // Decode HTML entities
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .trim();
}

/**
 * Collect all citation keys from ALL documents in the project.
 * This is needed because the bibliography page might not contain any citations,
 * but references items cited in other chapters.
 */
async function getAllCitedKeysInProject(projectId: string): Promise<string[]> {
  const { db } = await import('./indexedDBService');

  // Get all documents for this project
  const allDocs = await db.documents.getByProject(projectId);
  const allKeys = new Set<string>();

  for (const doc of allDocs) {
    try {
      // Load document content
      const content = await db.contents.get(doc.id);
      if (content) {
        const keys = extractCitationKeysFromMarkdown(content);
        keys.forEach(k => allKeys.add(k));
      }
    } catch (error) {
      console.warn('[placeholderService] Error loading doc for bibliography:', doc.id);
    }
  }

  return Array.from(allKeys);
}

/**
 * Resolve {{bibliography}} placeholder in markdown text.
 * This is async because it needs to call citationService.
 *
 * @param markdown - The markdown text containing {{bibliography}}
 * @param projectId - The project ID to get bibliography items from
 * @returns The markdown with {{bibliography}} replaced by formatted entries
 */
export async function resolveBibliographyPlaceholder(
  markdown: string,
  projectId: string,
  cslStyle?: string
): Promise<string> {
  console.log('[placeholderService] resolveBibliographyPlaceholder called, markdown length:', markdown.length);
  console.log('[placeholderService] Looking for {{bibliography}} in:', markdown.substring(0, 500));

  // Check if there's a bibliography placeholder
  const hasBibPlaceholder = markdown.toLowerCase().includes('{{bibliography}}');
  console.log('[placeholderService] Has {{bibliography}}:', hasBibPlaceholder);

  if (!hasBibPlaceholder) {
    return markdown;
  }

  // Reset regex state
  BIBLIOGRAPHY_REGEX.lastIndex = 0;

  try {
    // Import services dynamically to avoid circular dependencies
    const { citationService } = await import('./citationService');
    const { bibliographyService, zoteroDataToCSL } = await import('./bibliographyService');

    // Set CSL style if provided
    if (cslStyle) {
      console.log('[placeholderService] Loading CSL style, length:', cslStyle.length);
      citationService.setStyle(cslStyle);
    } else {
      console.warn('[placeholderService] No CSL style provided, using default');
    }

    // Extract citation keys from ALL project documents (not just current one)
    // This is important because bibliography page itself usually has no citations
    const citedKeys = await getAllCitedKeysInProject(projectId);

    console.log('[placeholderService] Found', citedKeys.length, 'cited keys in project:', citedKeys);

    if (citedKeys.length === 0) {
      // No citations found in any document
      return markdown.replace(BIBLIOGRAPHY_REGEX, '');
    }

    // Load items from bibliography service and register with citationService
    const cslItems: any[] = [];
    for (const key of citedKeys) {
      const zoteroItem = await bibliographyService.getItem(projectId, key);
      if (zoteroItem) {
        const cslItem = zoteroDataToCSL(zoteroItem);
        cslItems.push(cslItem);
      }
    }

    console.log('[placeholderService] Loaded', cslItems.length, 'CSL items from bibliography');

    if (cslItems.length > 0) {
      await citationService.registerCSLItemsAsync(cslItems);
    }

    // Generate bibliography using citeproc-js
    const bibResult = await citationService.makeBibliographyForKeys(citedKeys);

    console.log('[placeholderService] Generated', bibResult.entries.length, 'bibliography entries');

    if (bibResult.entries.length === 0) {
      return markdown.replace(BIBLIOGRAPHY_REGEX, '');
    }

    // Convert HTML entries to plain text
    const plainTextEntries = bibResult.entries.map(entry => stripHtmlTags(entry));

    // Join with double newlines (empty line between each entry)
    const bibliographyText = plainTextEntries.join('\n\n');

    // Replace {{bibliography}} placeholder with the generated text
    return markdown.replace(BIBLIOGRAPHY_REGEX, bibliographyText);

  } catch (error) {
    console.error('[placeholderService] Error resolving bibliography:', error);
    // On error, remove the placeholder to avoid breaking export
    return markdown.replace(BIBLIOGRAPHY_REGEX, '');
  }
}
